import React, { createContext, useContext, useMemo } from "react";
import type { Vault77HealthIssue, Vault77HealthReport } from "./types";

export interface ReviewHealthContextValue {
    /**
     * Returns the health findings attached to a FieldResponse review id — the id the Reviews tab
     * shows on each row (`reviewListItem.EmployeeReviewId`). Empty when the review is clean, the id is
     * unknown, or there is no provider above this consumer.
     */
    getReviewIssues: (externalReviewId: number | null | undefined) => Vault77HealthIssue[];
}

const EMPTY: Vault77HealthIssue[] = [];

export const ReviewHealthContext = createContext<ReviewHealthContextValue>({
    getReviewIssues: () => EMPTY,
});

/**
 * Indexes a Vault77 health report by FieldResponse review id (`ExternalReviewId`) so the Reviews tab
 * can flag the specific reviews that need work — the employee-row warning only says *which employee*
 * has a problem, not which review. Only review-scoped findings carry an `ExternalReviewId`
 * (REVIEW_WITHOUT_PAY_RATE / DUPLICATE_REVIEW_LINK / EFFECTIVE_DATE_DRIFT), so the index self-selects
 * exactly the issues that belong on a review row; employee-/rate-level findings (and the dangling-link
 * case, whose review is gone) are naturally excluded.
 *
 * Read-only and minimally invasive: it reuses the report the Employees page already holds in state — no
 * extra fetch — and degrades to "no marks" when there is no provider (the notification deep-link path
 * via ReviewsDecoupler, non-HR users, or shared-detail views that never load the report).
 */
export const ReviewHealthProvider: React.FC<{
    report: Vault77HealthReport | null;
    children: React.ReactNode;
}> = ({ report, children }) => {
    const value = useMemo<ReviewHealthContextValue>(() => {
        // ExternalReviewId is the FieldResponse review id and is globally unique across employees, so a
        // flat id -> issues map is enough; no need to scope by employee.
        const byExternalId = new Map<number, Vault77HealthIssue[]>();
        if (report) {
            for (const emp of report.Employees) {
                for (const issue of emp.Issues) {
                    if (issue.ExternalReviewId == null) continue;
                    const list = byExternalId.get(issue.ExternalReviewId);
                    if (list) list.push(issue);
                    else byExternalId.set(issue.ExternalReviewId, [issue]);
                }
            }
        }
        return {
            getReviewIssues: (externalReviewId) =>
                externalReviewId == null ? EMPTY : byExternalId.get(externalReviewId) || EMPTY,
        };
    }, [report]);

    return <ReviewHealthContext.Provider value={value}>{children}</ReviewHealthContext.Provider>;
};

/** Hook form for functional consumers (class consumers can use `static contextType`). */
export function useReviewHealth(externalReviewId: number | null | undefined): Vault77HealthIssue[] {
    return useContext(ReviewHealthContext).getReviewIssues(externalReviewId);
}
